import React from "react";
import { Link } from "react-router-dom";
import { 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Twitter,
  ChevronRight
} from "lucide-react";
import brandLogo from "../images/logo.png";

const ContactUs = () => {
  const mapStyle = { border: 0, filter: 'grayscale(1) brightness(0.8) contrast(1.2)' };

  return (
    <div className="min-h-screen bg-black text-white font-outfit selection:bg-red-600/30 overflow-x-hidden">
      
      {/* ── LUXURY HEADER ── */}
      <section className="relative pt-40 pb-32 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 to-transparent" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/5 blur-[150px] rounded-full" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-red-500 text-[10px] font-black tracking-[0.4em] uppercase mb-12 hover:gap-4 transition-all">
            <ArrowRight className="rotate-180" size={14} /> Back to Hub
          </Link>
          <h1 className="text-6xl md:text-[8rem] font-bold tracking-tighter leading-[0.85] mb-12">
            GLOBAL <br/> <span className="text-primary-gradient">NODES.</span>
          </h1>
          <p className="text-gray-400 text-xl md:text-2xl font-light max-w-2xl leading-relaxed">
            Direct liaison channels for our strategic headquarters and regional operations. Connect with our architects across the global corridor.
          </p>
        </div>
      </section>

      {/* ── LIAISON CHANNELS ── */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            <div className="p-10 rounded-[2.5rem] glass border border-white/5 group hover:border-red-500/30 transition-all">
              <Phone className="text-red-600 mb-6" size={32} />
              <h4 className="text-gray-500 text-[10px] font-black tracking-widest uppercase mb-4">Direct Phone</h4>
              <p className="text-2xl font-bold text-white">+971 4 565 0900</p>
            </div>
            <div className="p-10 rounded-[2.5rem] glass border border-white/5 group hover:border-red-500/30 transition-all">
              <Mail className="text-red-600 mb-6" size={32} />
              <h4 className="text-gray-500 text-[10px] font-black tracking-widest uppercase mb-4">Secure Mail</h4>
              <p className="text-2xl font-bold text-white lowercase">info@myb.ae</p>
            </div>
            <div className="p-10 rounded-[2.5rem] glass border border-white/5 group hover:border-red-500/30 transition-all">
              <div className="flex gap-4 mb-6">
                {[Facebook, Instagram, Linkedin, Twitter].map((Icon, i) => (
                  <Icon key={i} className="text-gray-400 hover:text-white transition-colors cursor-pointer" size={20} />
                ))}
              </div>
              <h4 className="text-gray-500 text-[10px] font-black tracking-widest uppercase mb-4">Ecosystem</h4>
              <p className="text-sm font-bold text-white tracking-widest uppercase flex items-center gap-2">Operational Status <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /></p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DUAL MAP ARCHITECTURE ── */}
      <section className="py-32 bg-[#050505] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* NODE 01: DUBAI HQ */}
            <div className="reveal-on-scroll">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-[1px] bg-red-600" />
                <span className="text-red-500 text-[10px] font-black tracking-[0.4em] uppercase text-white">Global Node 01 / Dubai HQ</span>
              </div>
              <div className="h-[500px] rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl mb-10 group relative">
                <iframe 
                  src="https://maps.google.com/maps?q=25.2620039,55.3286625&z=17&output=embed" 
                  width="100%" 
                  height="100%" 
                  style={mapStyle} 
                  allowFullScreen="" 
                  loading="lazy">
                </iframe>
                <div className="absolute bottom-6 left-6 right-6 p-6 glass rounded-2xl border border-white/10 backdrop-blur-3xl opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none">
                  <p className="text-xs text-white font-bold uppercase tracking-widest mb-2">Primary Operations Hub</p>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Mai Tower, Floor 7, Al Nahda 1, Dubai, UAE</p>
                </div>
              </div>
              <p className="text-gray-400 font-light text-lg">Our strategic headquarters for Middle Eastern markets and global corporate architecture.</p>
            </div>

            {/* NODE 02: INDIA OPS */}
            <div className="reveal-on-scroll">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-[1px] bg-red-600" />
                <span className="text-red-500 text-[10px] font-black tracking-[0.4em] uppercase text-white">Global Node 02 / India Ops</span>
              </div>
              <div className="h-[500px] rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl mb-10 group relative">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m23!1m12!1m3!1d44597.72238254596!2d76.63884986730056!3d8.887123981172701!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m8!3e2!4m0!4m5!1s0x3b05e30f4115d7ff%3A0xac4e7cb060dd4832!2sSupreme%20Supermarket!3m2!1d8.8916963!2d76.6805148!5e0!3m2!1sen!2sin!4v1762937053128!5m2!1sen!2sin" 
                  width="100%" 
                  height="100%" 
                  style={mapStyle} 
                  allowFullScreen="" 
                  loading="lazy">
                </iframe>
                <div className="absolute bottom-6 left-6 right-6 p-6 glass rounded-2xl border border-white/10 backdrop-blur-3xl opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none">
                  <p className="text-xs text-white font-bold uppercase tracking-widest mb-2">Regional Support Core</p>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Strategic technical support and regional logistics hub.</p>
                </div>
              </div>
              <p className="text-gray-400 font-light text-lg">Backend logistics and auxiliary operations ensuring global consistency and support.</p>
            </div>

          </div>
        </div>
      </section>

      {/* ── PREMIUM FOOTER ── */}
      <footer className="bg-black pt-32 pb-16 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-20 mb-20">
            <div className="max-w-sm">
              <img src={brandLogo} alt="Make Your Brand" className="h-40 w-auto object-contain mb-10 brightness-110"/>
              <p className="text-gray-500 text-lg font-light leading-relaxed">
                Dubai's premier business architecture firm. Engineering sustainable corporate empires.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-24">
              <div>
                <h4 className="text-white font-bold text-xs tracking-widest uppercase mb-10">Liaison</h4>
                <div className="space-y-6 text-gray-500 text-sm font-bold tracking-widest">
                  <p className="flex gap-4 items-center"><Phone size={14} className="text-red-500"/> +971 4 565 0900</p>
                  <p className="flex gap-4 items-center lowercase"><Mail size={14} className="text-red-500"/> info@myb.ae</p>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-16 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-black tracking-[0.4em] uppercase text-gray-700">
            <p>© {new Date().getFullYear()} MAKE YOUR BRAND BUSINESS SERVICES L.L.C.</p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default ContactUs;
